/*
  Чек-лист: что для судебного пакета уже есть, а чего нет.

  🔴 Указание владельца 18.09.2026: «чтобы сформировать пакет документов, нужно,
  чтобы человек загрузил эти данные или заполнил вручную. Для этого где-то нужно
  отобразить, что уже есть, а чего нет».

  До этого блока узнать о пропуске можно было одним способом - попробовать
  сформировать пакет по счёту и прочитать отказ. На портфеле в пять тысяч счетов
  так не проверяют: о том, что дом не заведён в справочнике, человек узнавал в
  день подачи.

  Три правила, по которым блок устроен.

  **Он ничего не запрещает.** Владелец сказал прямо: блок не жёсткий, юрист
  вправе дописать недостающее руками и подать без платформы. Поэтому кнопок
  здесь нет, действий он не блокирует, а необязательные строки на общий вывод не
  влияют.

  **Считает сервер, показывает кабинет.** Состояние каждой строки приходит с
  сервера тем же перечнем, которым проверяет себя сборщик пакета
  (`data_readiness_service`): свой второй список обязательного разъехался бы с
  первым, и чек-лист обещал бы готовность там, где документ не собирается.

  🔴 Запрос живёт в странице, а не здесь (30.09.2026). С того дня готовность
  читают двое - этот блок и лента шагов, - и, грузи каждый сам, на время записи
  файла на одном экране висели бы два разных ответа о том, что уже есть.

  **Числа приходят числами.** Сервер отдаёт «покрыто» и «всего», а фразу со
  склонением собирает кабинет - правило склонения здесь одно на весь экран
  (`api/format.js`).
*/

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { accountsDative, accountsGenitive, dateTime, plural } from '../api/format.js'
import { Loading, Notice, Tag } from './Common.jsx'
import Disclosure from './Disclosure.jsx'
import Modal from './Modal.jsx'

const STATE_TITLES = {
  ready: 'есть',
  partial: 'частично',
  missing: 'нет',
}

/* Цвет метки. Красный достаётся только обязательному: строка «желательно»,
   покрашенная в цвет потери денег, обесценивает сам цвет - и настоящую нехватку
   человек перестаёт замечать. */
function tone(item) {
  if (item.state === 'ready') return 'calm'
  if (!item.required) return 'muted'
  return item.state === 'partial' ? 'warn' : 'burn'
}

const SOURCE_NOTES = {
  none:
    'Коннектор 1С не настроен: данные вносите файлами на этой странице или руками ' +
    'в карточке счёта.',
  idle:
    'Ключ обмена выдан, но 1С ещё ни разу не выходила на связь. Пока обмен не пошёл, ' +
    'данные вносите файлами или руками.',
  working: 'Обмен с 1С идёт: срез приходит целиком каждую ночь.',
}

const debtors = (count) => plural(count, 'должника', 'должников', 'должников')

/* Строка домов считает и участок, и протокол (19.10.2026): привязанный дом без
   протокола пакета не даёт, и сколько таких домов, сервер присылает числом. */
const withoutProtocol = (count) =>
  `${plural(count, 'дом', 'дома', 'домов')} без протокола общего собрания.`

/* Покрытие словами. Ноль показывается отдельной фразой, а не «по 0 счетам»:
   «есть по 0 счетам из 47» человек читает дольше, чем «пока нет ни одного». */
function coverage({ covered, total, counted_in: countedIn }) {
  if (total === 0) {
    return countedIn === 'people'
      ? 'Должников в портфеле пока нет.'
      : 'Портфель пуст: счетов ещё нет.'
  }
  if (covered === 0) {
    return countedIn === 'people'
      ? `Пока ни у одного из ${debtors(total)}.`
      : `Пока ни по одному из ${accountsGenitive(total)}.`
  }
  return countedIn === 'people'
    ? `Есть у ${debtors(covered)} из ${total}.`
    : `Есть по ${accountsDative(covered)} из ${total}.`
}

/*
  🔴 Плитка показывает ТОЛЬКО состояние, а объяснение прячет под «Подробнее»
  (указание владельца 18.09.2026, второй заход по этому экрану).

  Первая раскладка выводила «чем грозит» и перечень источников прямо в плитке:
  у несобранного требования это пятнадцать строк, семь плиток подряд, и весь
  экран загрузки превращался в стену текста, из которой не читалось главное -
  что собрано, а что нет. Само объяснение нужно, но нужно оно один раз и по
  одному требованию, а не сразу по всем.

  Поэтому в плитке остаются три вещи, которые человек сравнивает взглядом:
  название, состояние и покрытие числом. Остальное - по нажатию.

  🔴 Объяснение открывается ОКНОМ, а не внутри плитки (указание владельца
  18.09.2026: «выровняй их по высоте, чтобы они были одинаковые»). Плитки
  одинаковы всегда, и раскрытие не должно этого ломать: раскройся объяснение
  внутри, ряд растянулся бы по самой высокой плитке, а соседки превратились бы в
  пустые рамки в триста пикселей. Окно оставляет сетку неподвижной и на телефоне
  показывает объяснение там же, где нажали, а не под всеми семью плитками.
*/
function Row({ item, onDetails }) {
  const done = item.state === 'ready'

  return (
    <li className="readiness__row">
      {/* Метки стоят ПОД названием, а не рядом с ним. В строку с названием они
          вставали по-разному: у короткого - рядом, у длинного - переносом на
          вторую строку, и ряд плиток читался как собранный по-разному
          (осмотр 18.09.2026). */}
      <div className="readiness__title">{item.title}</div>
      <div className="readiness__tags">
        <Tag kind={tone(item)}>{STATE_TITLES[item.state]}</Tag>
        {item.required ? null : <Tag kind="muted">желательно</Tag>}
      </div>
      <div className="readiness__fact">
        {item.fact ?? coverage(item)}
        {item.houses_without_protocol ? ` ${withoutProtocol(item.houses_without_protocol)}` : ''}
      </div>
      {/* Дома без протокола исправляют в таблице домов участка, а не здесь:
          ссылка ведёт туда сразу, мимо окна «Подробнее» (19.10.2026). */}
      {item.houses_without_protocol ? (
        <Link className="readiness__link" to="/settings#courts">
          Показать дома без протокола
        </Link>
      ) : null}

      {/* У собранного требования объяснять нечего: «чем грозит» и «где взять»
          читают, когда чего-то не хватает. */}
      {done ? null : (
        <button
          type="button"
          className="link-button readiness__more"
          onClick={() => onDetails(item.key)}
        >
          Подробнее
        </button>
      )}
    </li>
  )
}

/* Окно с объяснением одного требования: чем грозит пропуск и откуда берутся
   сведения. Ссылка «где заполнить» стоит первой кнопкой - это то, зачем окно и
   открывали; «Закрыть» названо по тому, что сделает (правило чек-листа формы). */
function Details({ item, onClose }) {
  return (
    <Modal title={item.title} onClose={onClose}>
      <p className="readiness__risk">{item.risk}</p>
      <div className="section__title section__title--spaced">Откуда это берётся</div>
      <ul className="readiness__sources">
        {item.sources.map((source) => (
          <li key={source}>{source}</li>
        ))}
      </ul>
      <div className="row row--tight">
        {item.where ? (
          <Link className="button" to={item.where}>
            {item.where_title}
          </Link>
        ) : null}
        <button type="button" className="button button--quiet" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </Modal>
  )
}

export default function ReadinessChecklist({ state, error }) {
  const [shown, setShown] = useState(null)

  // Сбой повторного чтения не стирает требования, которые уже показаны
  // (аудит 26.10.2026, причина 1): плашка встаёт над ними.
  if (!state) {
    return error ? <Notice error={error} /> : <Loading what="Смотрим, чего не хватает для пакета" />
  }

  const required = state.items.filter((item) => item.required)
  const done = required.filter((item) => item.state === 'ready').length

  /*
    🔴 Блок свёрнут в кнопку (21.09.2026, указание владельца) - тем же приёмом,
    что памятка «Где это взять» рядом и объяснение решения на карточке счёта.
    Семь требований с пояснениями занимали верх экрана загрузки, а читают их
    внимательно один раз: когда разбираются, почему пакет не собрался.

    Счётчик стоит в самом заголовке, поэтому свёрнутый блок не молчит:
    «собрано 5 из 7» - это и есть ответ, за которым сюда приходят чаще всего.
  */
  return (
    <Disclosure
      title="Что нужно для судебного пакета"
      counter={`собрано ${done} из ${required.length}`}
    >
      <Notice error={error} />
      <p className="readiness__source">
        {SOURCE_NOTES[state.source.connector]}
        {state.source.last_exchange_at
          ? ` Последняя передача - ${dateTime(state.source.last_exchange_at)}.`
          : ''}
      </p>

      <p className="readiness__score">
        {state.is_ready
          ? 'Данных для пакета хватает.'
          : 'Пока собрано не всё, документы по части счетов не сформируются.'}
      </p>

      {/* Несобранное впереди. Порядок сервера - это порядок заполнения, а
          раздел открывают с вопросом «чего ещё не хватает»; собранное человек
          уже видел. Сортировка устойчивая: внутри каждой половины порядок
          остаётся серверным и меняется только вместе с состоянием требования. */}
      <ul className="readiness__list">
        {[...state.items]
          .sort((one, two) => (one.state === 'ready') - (two.state === 'ready'))
          .map((item) => (
            <Row key={item.key} item={item} onDetails={setShown} />
          ))}
      </ul>

      <p className="muted">
        Это не запрет: незаполненное юрист вправе дописать в самом заявлении и подать без
        Арбитра ЖКХ. Список показывает, чего не хватает Арбитру ЖКХ, чтобы собрать пакет за вас,
        и чем грозит пропуск в суде.
      </p>

      {/* Требование ищется по ключу, а не хранится объектом: после загрузки
          файла ответ сервера приходит заново, и объект в состоянии остался бы
          прежним - с цифрами до записи. */}
      {shown ? (
        <Details
          item={state.items.find((item) => item.key === shown)}
          onClose={() => setShown(null)}
        />
      ) : null}
    </Disclosure>
  )
}
